/**
 * Design tokens. Every colour the interface uses is named here, and no
 * screen hard-codes a hex value — which is what made this re-skin a change
 * to one file.
 *
 * THE PALETTE IS THE FIRM'S, NOT A CHOICE MADE HERE
 *
 *   navy        #192537    beige-gold  #B09E80
 *   dark gold   #716551    light grey  #E9E9E9    white #FFFFFF
 *
 * Everything below is one of those five or a tint of one. The interface
 * needs more steps than five — a hover, a divider, a muted label — so the
 * extra values are lightened or darkened from a named colour rather than
 * drawn from a new hue. Nothing here introduces a sixth colour.
 *
 * THE ONE RULE THAT SHAPES ALL OF IT
 *
 * Beige-gold is an accent, never text. Measured, not assumed:
 *
 *   #B09E80 on white   2.6 : 1   fails — fills, rules and bars only
 *   #716551 on white   5.7 : 1   passes — this is the gold that carries text
 *   white on #716551   5.7 : 1   passes — gold fills take white text
 *   navy  on #B09E80   5.9 : 1   passes — beige-gold fills take navy text
 *
 * So `accent` is the beige-gold and is only ever a fill or a rule, and
 * `ink` is the dark gold and is what any gold *text* or any gold fill
 * carrying white text uses. Putting white on `accent` is the mistake this
 * split exists to prevent.
 */

/** Beige-gold. Fills, rules and bars. Never text, never white on top. */
export const accent = '#B09E80';

/** Dark gold. Gold text, and gold fills that carry white text. */
export const ink = '#716551';

export const theme = {
  accent,
  ink,

  /*
    Light grey behind, white cards on top. The firm's grey is a flat
    neutral, so the cards are what carry the depth — hence the shadows
    below being navy-tinted rather than black, which would grey them.
  */
  canvas: '#E9E9E9',
  surface: '#FFFFFF',
  surfaceMuted: '#F7F7F8',
  surfaceHeader: '#F9F9FA',
  surfaceSunken: '#F1F1F3',

  /*
    Text is navy, down through tints of it. The firm's guidance allows
    near-black or navy for body text; navy ties the reading surface to
    the sidebar instead of leaving two unrelated darks on one screen.
  */
  text: '#192537',
  textMuted: '#5A6676',
  textSubtle: '#7A8694',
  textFaint: '#95A0AC',
  label: '#3A4654',

  border: '#E2E2E5',
  borderStrong: '#D4D4D8',
  divider: '#EDEDEF',
  dividerSoft: '#E8E8EB',

  /*
    The sidebar is the brand navy itself, with the rest of the rail
    derived from it. navText clears 7.4 : 1 on that navy and navTextDim
    5.1 : 1, so the quieter one is still readable rather than decorative.
  */
  nav: '#192537',
  navDeep: '#121B29',
  navRaised: '#22304A',
  navHover: '#2A3A57',
  navBorder: '#283650',
  navText: '#A9B5C7',
  navTextDim: '#8795AA',
  navTextBright: '#F2F5FA',

  /*
    Status, on the firm's own muted pair rather than the brighter green
    and red this interface used before. Each `Deep` is the text weight and
    clears 7 : 1 on white; the plain one is for dots and fills.
  */
  good: '#4A7C59',
  goodDeep: '#35603F',
  goodBg: '#EDF3EE',
  goodBorder: '#CBDDD1',

  /*
    Amber is the one functional colour not in the five, and it is here
    deliberately. Warning has to be distinguishable from both "fine" and
    "wrong", and from the gold accent — a warning rendered in the brand
    gold would be indistinguishable from an active tab. Kept muted so it
    sits beside the palette rather than shouting over it.
  */
  warn: '#B4801E',
  warnDeep: '#7E590F',
  warnBg: '#FAF3E4',

  bad: '#A94442',
  badDeep: '#8A3230',
  badBg: '#F8EDEC',

  /* -------------------------------------------------------------------------
     Chart colours.

     Separate from the status colours above on purpose. Those are for text and
     pills; these are for marks on a plot, and the firm's own rule is that the
     green/red pair appears in charts and dashboards only, never in document
     text. See the organisation's brand guidance.

     Every set below was run through the data-viz validator rather than chosen
     by eye — lightness band, chroma floor, colour-vision separation and
     contrast against the white card they sit on. They are unchanged by the
     re-skin: re-tinting a validated set to match a brand would throw away the
     property that made it validated.
  ------------------------------------------------------------------------- */

  /*
    Polarity: made money / lost money. The firm's own pair.

    As a pure colour pair these two are NOT distinguishable under red-green
    colour blindness — the validator puts them at ΔE 3.5, far below the 8 it
    wants. They are used anyway because the firm's guidance names them, and
    because in the only place they appear the colour is not doing the work:
    a profit column sits above the zero line and a loss below it, and both
    carry a printed figure. Never use this pair where position and label do
    not already say the same thing.
  */
  chartGood: '#4A7C59',
  chartBad: '#A94442',

  /*
    Two things being compared — tax collected against tax paid. A blue and an
    amber, which the validator separates at ΔE 18.6 under protanopia.
  */
  chartSeries: ['#2A6FA8', '#C2762B'],

  /*
    Ageing, light to dark. One hue, so the ramp itself says "older" without
    needing a second colour to mean "bad" — the order carries it. Five steps
    with a visible lightness gap between each, and the lightest still clears
    the white card behind it.
  */
  chartRamp: ['#9FB6D2', '#7E9CC0', '#5F82AC', '#426796', '#254D80'],

  /** Axis lines and gridlines. Present, never competing with the data. */
  chartAxis: '#DCDCE0',

  /*
    Navy-tinted rather than black. A black shadow on a grey canvas turns
    the card edge muddy; tinting it with the sidebar colour keeps the
    whole screen in one family.
  */
  shadowCard: '0 12px 32px rgba(25,37,55,.09)',
  shadowPill: '0 6px 18px rgba(25,37,55,.07)',
  /** The lift under the active sidebar item, in its own gold. */
  shadowNavActive: '0 8px 20px rgba(113,101,81,.42)',

  radiusCard: '18px',
  radiusControl: '14px',
  radiusPill: '999px',

  fontBody: "'Figtree','IBM Plex Sans Arabic',system-ui,sans-serif",
  fontMono: "'IBM Plex Mono',monospace",
  fontArabic: "'IBM Plex Sans Arabic',sans-serif",
} as const;

/* ---------------------------------------------------------------------------
   Status pills: [background, border, text].

   Four families, so a glance tells you which kind of thing you are looking
   at before you read it:

     grey    nothing has happened yet, or nothing will    draft, voided
     navy    in progress, waiting on somebody             queued, awaiting
     gold    needs a person to look                       review, approval
     green   done                                         paid, in Xero
     red     wrong                                        failed, overdue

   Every text colour clears 4.5 : 1 on its own background.
--------------------------------------------------------------------------- */
export const PILLS: Record<string, readonly [string, string, string]> = {
  DRAFT: ['#F0F0F2', '#E3E3E7', '#515C6B'],
  READY: ['#EDF1F7', '#D6DFEC', '#2B4568'],
  QUEUED: ['#EDF1F7', '#D6DFEC', '#2B4568'],
  REVIEW: ['#F8F3E7', '#E9DEC4', '#7E590F'],
  BLOCKED: ['#F8EDEC', '#EBD2D1', '#8A3230'],
  'IN XERO': ['#EDF3EE', '#CBDDD1', '#35603F'],
  // PAID and OVERDUE already exist further down. Awaiting payment is
  // deliberately quiet: it is the ordinary state of most invoices and should
  // not compete with the one that needs chasing.
  'AWAITING PAYMENT': ['#F1F3F6', '#DEE3EA', '#44536B'],
  // Part way there: firmer than awaiting, not as loud as overdue.
  'PART PAID': ['#EDF1F7', '#D6DFEC', '#2B4568'],
  'AWAITING APPROVAL': ['#F8F3E7', '#E9DEC4', '#7E590F'],
  // Quiet because it is grey, not because it is faint. The lighter text
  // this used to carry sat at 3.3 : 1, which is not readable — a voided
  // document should recede, not disappear.
  VOIDED: ['#F0F0F2', '#E3E3E7', '#5A6676'],
  PUSHED: ['#EDF3EE', '#CBDDD1', '#35603F'],
  AUTHORISED: ['#EDF3EE', '#CBDDD1', '#35603F'],
  FAILED: ['#F8EDEC', '#EBD2D1', '#8A3230'],
  CHECKING: ['#F0F0F2', '#E3E3E7', '#515C6B'],
  '200': ['#EDF3EE', '#CBDDD1', '#35603F'],
  '400': ['#F8EDEC', '#EBD2D1', '#8A3230'],
  '429': ['#F8F3E7', '#E9DEC4', '#7E590F'],
  // Sent, not yet answered. The brand gold, which is what gold is for.
  SENT: ['#F6F2EA', '#E4DBC9', '#716551'],
  PAID: ['#EDF3EE', '#CBDDD1', '#35603F'],
  ACCEPTED: ['#EDF3EE', '#CBDDD1', '#35603F'],
  BILLED: ['#EDF3EE', '#CBDDD1', '#35603F'],
  OK: ['#EDF3EE', '#CBDDD1', '#35603F'],
  TRACKED: ['#EDF3EE', '#CBDDD1', '#35603F'],
  UNTRACKED: ['#F0F0F2', '#E3E3E7', '#515C6B'],
  LOW: ['#F8F3E7', '#E9DEC4', '#7E590F'],
  OVERDUE: ['#F8EDEC', '#EBD2D1', '#8A3230'],
  DECLINED: ['#F8EDEC', '#EBD2D1', '#8A3230'],
  REORDER: ['#F8EDEC', '#EBD2D1', '#8A3230'],
};

/**
 * Look up how to present a connection's health, tolerating not knowing.
 *
 * Reading CONNECTION_STYLES[state] directly is what blanked the entire
 * application: /api/me does not send a state, the lookup returned undefined,
 * and .label on undefined threw during render — so React unmounted everything
 * and the page went white. A missing field should degrade to "we don't know",
 * never to a blank screen.
 */
export function connectionStyle(state: string | null | undefined) {
  return (
    CONNECTION_STYLES[state as keyof typeof CONNECTION_STYLES] ?? {
      label: 'Unknown',
      dot: '#95A0AC',
      bg: '#F0F0F2',
      bd: '#E3E3E7',
      fg: '#515C6B',
    }
  );
}

/** Connection-health presentation, keyed by ConnectionState. */
export const CONNECTION_STYLES = {
  OK: { label: 'Healthy', dot: '#4A7C59', bg: '#EDF3EE', bd: '#CBDDD1', fg: '#35603F' },
  EXPIRING: { label: 'Expiring soon', dot: '#B4801E', bg: '#FAF3E4', bd: '#EADFC2', fg: '#7E590F' },
  FAILED: { label: 'Refresh failed', dot: '#A94442', bg: '#F8EDEC', bd: '#EBD2D1', fg: '#8A3230' },
  REAUTH: { label: 'Re-authorise', dot: '#8A3230', bg: '#F5E4E3', bd: '#E4C6C5', fg: '#732826' },
} as const;
